// src/pages/admin/Orders/OrderDetail.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Package, Mail, Phone, MapPin } from 'lucide-react';
import AdminHeader from '../../../components/admin/common/AdminHeader';
import AdminBreadcrumb from '../../../components/admin/common/AdminBreadcrumb';
import StatusBadge from '../../../components/admin/common/StatusBadge';
import { orderService, type Order } from '../../../services/orderService';
import { formatPrice } from '../../../utils/helpers';

const OrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    fetchOrder();
  }, [id]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const orderData = await orderService.getOrder(id!);
      if (orderData) {
        setOrder(orderData);
      } else {
        setError('Order not found');
      }
    } catch (err) {
      console.error('Error fetching order:', err);
      setError('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-64 bg-gray-200 rounded"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="p-6">
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">
          {error || 'Order not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <AdminBreadcrumb 
        items={[
          { label: 'Orders', href: '/admin/orders' },
          { label: `Order #${order.orderNumber}` }
        ]} 
      />

      <AdminHeader 
        title={`Order #${order.orderNumber}`}
        actions={
          <div className="flex items-center space-x-4">
            <StatusBadge status={order.status} />
            <select
  value={order.status}
  onChange={async (e) => {
    const newStatus = e.target.value as Order['status'];
    try {
      await orderService.updateOrderStatus(order.id, newStatus);
      fetchOrder();
    } catch (err) {
      console.error('Error updating status:', err);
    }
  }}
  className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-blue-500"
>
  <option value="pending">Pending</option>
  <option value="processing">Processing</option>
  <option value="completed">Completed</option>
  <option value="cancelled">Cancelled</option>
</select>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Order Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Items */}
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b">
              <h3 className="text-lg font-medium">Order Items</h3>
            </div>
            <div className="divide-y">
              {order.items.map((item) => (
                <div key={item.id} className="p-6 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <Package className="w-10 h-10 text-gray-400" />
                    <div>
                      <p className="font-medium">{item.name}</p>
                      <p className="text-sm text-gray-500">
                        {formatPrice(item.price)} × {item.quantity}
                      </p>
                    </div>
                  </div>
                  <div className="text-right font-medium">
                    {formatPrice(item.price * item.quantity)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Customer Info */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium mb-6">Customer Information</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium mb-4">Contact Details</h4>
                <div className="space-y-3">
                  <div className="flex items-center text-gray-600">
                    <Mail className="w-5 h-5 mr-2" />
                    {order.customer.email}
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Phone className="w-5 h-5 mr-2" />
                    {order.customer.phone}
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-medium mb-4">Shipping Address</h4>
                <div className="flex items-start text-gray-600">
                  <MapPin className="w-5 h-5 mr-2 mt-1" />
                  <div>
                    <div>{order.shippingAddress.street}</div>
                    <div>{order.shippingAddress.city}, {order.shippingAddress.state}</div>
                    <div>{order.shippingAddress.country}, {order.shippingAddress.postalCode}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium mb-6">Order Summary</h3>
            <div className="space-y-4">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal</span>
                <span>{formatPrice(order.subtotal)}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Shipping</span>
                <span>{formatPrice(order.deliveryFee)}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Tax</span>
                <span>{formatPrice(order.tax)}</span>
              </div>
              <div className="pt-4 border-t flex justify-between font-medium">
                <span>Total</span>
                <span className="text-lg">{formatPrice(order.total)}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium mb-6">Payment & Shipping</h3>
            <div className="space-y-6">
              <div>
                <h4 className="font-medium mb-2">Payment Details</h4>
                <div className="space-y-2 text-gray-600">
                  <div>Method: {order.paymentMethod}</div>
                  <div>Status: <StatusBadge status={order.paymentStatus} size="sm" /></div>
                </div>
              </div>
              <div>
                <h4 className="font-medium mb-2">Shipping Details</h4>
                <div className="space-y-2 text-gray-600">
                  <div>Method: Standard Shipping</div>
                  <div>Status: {order.status}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetail;